import React from 'react'

interface UseTableSelectionOptions<T> {
  items: T[]
  selectedIds: string[]
  getId: (item: T) => string
  onSelectionChange: (ids: string[]) => void
}

export function useTableSelection<T>({
  items,
  selectedIds,
  getId,
  onSelectionChange,
}: UseTableSelectionOptions<T>) {
  // Calculate selectable items and header checkbox state
  const selectedSelectableIds = React.useMemo(
    () => selectedIds.filter((id) => items.some((item) => getId(item) === id)),
    [selectedIds, items, getId],
  )

  // Two-state header checkbox logic:
  // - If all items are selected -> checked (shows uncheck icon)
  // - Otherwise -> unchecked
  const areAllItemsSelected = React.useMemo(() => {
    return items.length > 0 && selectedSelectableIds.length === items.length
  }, [items.length, selectedSelectableIds.length])

  // Header checkbox click handler
  const handleHeaderCheckboxChange = React.useCallback(() => {
    if (areAllItemsSelected) {
      // If all are selected, unselect all
      onSelectionChange([])
    } else {
      // If not all are selected, select all items
      const allIds = items.map(getId)
      onSelectionChange(allIds)
    }
  }, [areAllItemsSelected, onSelectionChange, items, getId])

  // Individual item selection handler
  const handleItemSelection = React.useCallback(
    (itemId: string) => {
      const currentSelected = selectedIds
      const newSelected = currentSelected.includes(itemId)
        ? currentSelected.filter((id) => id !== itemId)
        : [...currentSelected, itemId]

      onSelectionChange(newSelected)
    },
    [selectedIds, onSelectionChange],
  )

  return {
    selectedSelectableIds,
    areAllItemsSelected,
    handleHeaderCheckboxChange,
    handleItemSelection,
  }
}
